// "Save to contact" for the Calculators tabs that have no qualification scenario
// (Payment, DTI, Buydown, DSCR): the inputs and results go onto the contact as a note.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { formatDate } from '../kit/time';
import ContactPicker from './ContactPicker';

// lines: [[label, text], ...] in the order they should read on the note.
export const calcNoteContent = (title, lines) =>
  [`${title} · ${formatDate(new Date().toISOString())}`, ...lines.map(([k, v]) => `${k}: ${v}`)].join('\n');

export default function CalcSaveToContact({ title, lines, testId = 'calc-save' }) {
  const [contactId, setContactId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);
  const content = calcNoteContent(title, lines);
  const body = content.split('\n').slice(1).join('\n'); // the date line moves with the clock

  const bodyRef = useRef(body);
  bodyRef.current = body;

  // Changed numbers make the saved note stale, so offer a fresh save.
  useEffect(() => { setSavedTo(null); }, [body]);

  const save = async () => {
    if (!contactId || saving) return;
    const savedBody = body;
    setSaving(true);
    try {
      // bfy_only: income and debt figures stay in BFY, even on a GHL contact (ticket 231).
      await api.post('/notes', { content, contact_id: contactId }, { params: { bfy_only: true } });
      toast({ title: 'Saved to contact' });
      // Numbers edited while the save was in flight: the note holds the old ones, so stay savable.
      if (savedBody === bodyRef.current) setSavedTo(contactId);
    } catch (e) {
      toast({ title: 'Could not save to contact', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm space-y-3 print:hidden" data-testid={testId}>
      <ContactPicker label="Save to contact" value={contactId} allowNone={false} testId={`${testId}-picker`}
        onChange={(id) => { if (!saving) { setContactId(id); setSavedTo(null); } }} />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={saving || !contactId || !!savedTo} className="btn-gold disabled:opacity-50" data-testid={`${testId}-button`}>
          {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save to contact
        </button>
        {savedTo && (
          <Link to={`/portal/contacts/${savedTo}?tab=communications`} className="text-sm underline" style={{ color: '#070B5F' }} data-testid={`${testId}-link`}>
            Saved as a note. Open the contact's Communications tab
          </Link>
        )}
      </div>
    </div>
  );
}
